import React, { useEffect, useMemo, useRef, useState } from "react";
import ColorPickerField from "./ColorPickerField";
import { DEFAULT_ONBOARDING_STAGE_COLOR } from "../colors";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { Close } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import UnsavedChangesInDrawerModal from "@/pages/settings/components/UnsavedChangesInDrawerModal";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";
import {
  useUpdateOnboardingStage,
  useUpdateOnboardingStagesOrder
} from "@/pages/settings/components/contractsSettings/hooks";
import { calculateNewStageOrder } from "@/pages/settings/components/shared/stageStudio/calculateNewStageOrder";
import { StagePosition } from "@/pages/settings/components/shared/stageStudio/types";
import { EditOnboardingStageFormData } from "../types";
import { getCurrentStagePosition, getPositionOptions } from "../utils";

const TRANSLATION_KEY = "settings.contracts.onboardingStudio";

type confirmationModalType = "moveStageForward" | "moveStageBackward";

interface Props {
  isOpen: boolean;
  onClose: (stageWasUpdated?: boolean) => void;
  stageData: OnboardingStage | null;
  allStages: OnboardingStage[];
}

const EditOnboardingStageDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  stageData,
  allStages
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateStage = useUpdateOnboardingStage();
  const updateOrder = useUpdateOnboardingStagesOrder();

  const formRef = useRef<HTMLFormElement>(null);
  const [isFormValid, setIsFormValid] = useState(!!stageData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<{
    modalType: confirmationModalType;
    onConfirm: () => void;
  } | null>(null);
  const [formSetValue, setFormSetValue] = useState<
    ((field: string, value: unknown) => void) | null
  >(null);
  const [color, setColor] = useState<string>(
    stageData?.stageColor || DEFAULT_ONBOARDING_STAGE_COLOR
  );

  useEffect(() => {
    if (!isOpen) setHasUnsavedChanges(false);
  }, [isOpen]);

  useEffect(() => {
    if (stageData)
      setColor(stageData.stageColor || DEFAULT_ONBOARDING_STAGE_COLOR);
  }, [stageData]);

  const handleColorChange = (next: string) => {
    setColor(next);
    setHasUnsavedChanges(true);
  };

  const positionOptions = useMemo(
    () => getPositionOptions(allStages, t, stageData || undefined),
    [allStages, t, stageData]
  );

  useEffect(() => {
    if (typeof formSetValue === "function" && stageData) {
      formSetValue(
        "positionInPipeline",
        getCurrentStagePosition(stageData, allStages)
      );
    }
  }, [formSetValue, stageData, allStages]);

  if (!stageData) return null;

  const hasPositionChange = (data: EditOnboardingStageFormData) => {
    const newOrder = calculateNewStageOrder(
      allStages,
      stageData.id,
      data.positionInPipeline
    );
    return (
      newOrder.length !== allStages.length ||
      newOrder.some((id, index) => id !== allStages[index].id)
    );
  };

  const hasActualChanges = (data: EditOnboardingStageFormData) =>
    data.name !== stageData.name ||
    color !== stageData.stageColor ||
    (data.description || "") !== (stageData.description || "") ||
    hasPositionChange(data);

  const getModalType = (
    data: EditOnboardingStageFormData
  ): confirmationModalType => {
    const currentIndex = allStages.findIndex((s) => s.id === stageData.id);

    let newIndex = -1;
    if (data.positionInPipeline === StagePosition.FIRST) {
      newIndex = 0;
    } else if (data.positionInPipeline === StagePosition.LAST) {
      newIndex = allStages.length - 1;
    } else if (data.positionInPipeline.startsWith(StagePosition.AFTER_PREFIX)) {
      const targetStageId = data.positionInPipeline.replace(
        StagePosition.AFTER_PREFIX,
        ""
      );
      const targetIndex = allStages.findIndex((s) => s.id === targetStageId);
      newIndex = targetIndex + 1;
    }

    return newIndex > currentIndex ? "moveStageForward" : "moveStageBackward";
  };

  const executeUpdate = async (data: EditOnboardingStageFormData) => {
    try {
      await updateStage.mutateAsync({
        pathParams: { stageId: stageData.id },
        data: {
          name: data.name,
          stageColor: color,
          description: data.description ?? null
        }
      });
      if (hasPositionChange(data)) {
        const newOrder = calculateNewStageOrder(
          allStages,
          stageData.id,
          data.positionInPipeline
        );
        await updateOrder.mutateAsync({ data: newOrder });
      }
      queryClient.invalidateQueries({
        queryKey: ["settings/contracts/stages"]
      });
      setHasUnsavedChanges(false);
      toast.success(
        t(`${TRANSLATION_KEY}.editStage.successToast`, {
          stageName: data.name
        }),
        { duration: 5000 }
      );
      onClose(true);
    } catch (error) {
      console.error("Failed to update onboarding stage:", error);
      toast.error(t(`${TRANSLATION_KEY}.editStage.errorToast`));
    }
  };

  const handleSubmit = async (data: EditOnboardingStageFormData) => {
    if (!hasActualChanges(data)) {
      setHasUnsavedChanges(false);
      onClose();
      return;
    }
    if (hasPositionChange(data)) {
      setPendingUpdateData({
        modalType: getModalType(data),
        onConfirm: () => executeUpdate(data)
      });
      setIsConfirmationModalOpen(true);
    } else {
      await executeUpdate(data);
    }
  };

  const handleConfirmReorder = () => {
    if (pendingUpdateData) {
      pendingUpdateData.onConfirm();
      setPendingUpdateData(null);
    }
    setIsConfirmationModalOpen(false);
  };

  const handleCancelReorder = () => {
    if (typeof formSetValue === "function") {
      formSetValue(
        "positionInPipeline",
        getCurrentStagePosition(stageData, allStages)
      );
    }
    setPendingUpdateData(null);
    setIsConfirmationModalOpen(false);
  };

  const handleSubmitButton = () => formRef.current?.requestSubmit();

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  const sections: FormSectionProps[] = [
    {
      id: "name-and-color",
      fields: [
        {
          name: "name",
          type: "text",
          required: true,
          label: t(`${TRANSLATION_KEY}.createStage.name`),
          layout: "flex",
          fieldClassName: "flex-1",
          defaultValue: stageData.name,
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.createStage.namePlaceholder`),
            onChange: () => setHasUnsavedChanges(true)
          }
        },
        {
          name: "color",
          type: "render",
          required: false,
          label: t(`${TRANSLATION_KEY}.createStage.color`),
          layout: "flex",
          fieldClassName: "w-[80px]",
          renderNode: (
            <ColorPickerField value={color} onChange={handleColorChange} />
          )
        }
      ],
      fieldsContainerClassName: "flex flex-row gap-6 items-end"
    },
    {
      id: "description",
      fields: [
        {
          name: "description",
          type: "textarea",
          required: false,
          label: t(`${TRANSLATION_KEY}.createStage.description`),
          defaultValue: stageData.description || "",
          fieldActionProps: {
            placeholder: t(
              `${TRANSLATION_KEY}.createStage.descriptionPlaceholder`
            ),
            minRows: 1,
            onChange: () => setHasUnsavedChanges(true)
          }
        }
      ]
    },
    {
      id: "position",
      fields: [
        {
          name: "positionInPipeline",
          type: "singleSelect",
          required: true,
          label: t(`${TRANSLATION_KEY}.createStage.position`),
          defaultValue: getCurrentStagePosition(stageData, allStages),
          fieldActionProps: {
            options: positionOptions,
            placeholder: t(`${TRANSLATION_KEY}.createStage.selectPosition`),
            variant: "form",
            clearable: false,
            onSelection: () => setHasUnsavedChanges(true)
          }
        }
      ]
    }
  ];

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
  };

  return (
    <>
      <Drawer
        open={isOpen}
        direction="right"
        fixed
        modal={false}
        disablePreventScroll={false}
        noBodyStyles={true}
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer>
          <DrawerHeader>
            <DrawerTitle>
              <Text variant="lgSubtitle">
                {t(`${TRANSLATION_KEY}.editStage.title`)}
              </Text>
            </DrawerTitle>
            <IconButton
              data-test="close-onboarding-stage-drawer"
              onClick={handleCancel}
              variant="iconNoBg"
              size="sm"
            >
              <Close />
            </IconButton>
          </DrawerHeader>
          <div className="p-5 w-full overflow-y-scroll">
            <FormNew
              key={stageData.id}
              sections={sections}
              isFormValid={setIsFormValid}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
              shouldNotResetOnSubmit
            />
          </div>
          <DrawerFooter>
            <div className="flex justify-end gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
              <Button
                variant="secondaryNoBg"
                onClick={handleCancel}
                data-test="onboarding-edit-cancel"
              >
                {t("cancel")}
              </Button>
              <Button
                disabled={!isFormValid || updateStage.isPending}
                onClick={handleSubmitButton}
                data-test="onboarding-edit-save"
              >
                {t("save")}
              </Button>
            </div>
          </DrawerFooter>
        </AppDrawerContent>
      </Drawer>
      <UnsavedChangesInDrawerModal
        isOpen={showUnsavedModal}
        setIsOpen={setShowUnsavedModal}
        handleSubmit={() => {
          setShowUnsavedModal(false);
          handleSubmitButton();
        }}
        handleCancel={() => {
          setHasUnsavedChanges(false);
          setShowUnsavedModal(false);
          onClose();
        }}
      />
      {pendingUpdateData ? (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          title={t(`${TRANSLATION_KEY}.${pendingUpdateData.modalType}.title`)}
          confirmationText={
            <div className="flex flex-col gap-4">
              <span>
                {t(`${TRANSLATION_KEY}.${pendingUpdateData.modalType}.message`)}
              </span>
              <span>
                {t(`${TRANSLATION_KEY}.${pendingUpdateData.modalType}.message2`)}
              </span>
            </div>
          }
          handleSubmit={handleConfirmReorder}
          handleCancel={handleCancelReorder}
          isPrimaryButton={true}
          submitButtonText={t(
            `${TRANSLATION_KEY}.${pendingUpdateData.modalType}.confirm`
          )}
        />
      ) : null}
    </>
  );
};

export default EditOnboardingStageDrawer;
